import { createSelector } from '@reduxjs/toolkit'
import filter from 'lodash/filter'
import includes from 'lodash/includes'
import sortBy from 'lodash/sortBy'

import { ListNameStatusMap, ListName } from 'apps/posting/models/Post/constants'
import { IPost, IPostsList } from 'apps/posting/postingInterfaces'
import { NodeType } from 'common/builder'
import { AttachmentBlockAttachmentType } from 'common/builder/blocks/blockConstants'
import { IBlock } from 'common/builder/blocks/blockInterfaces'
import { BlockType } from 'common/builder/constants/BlockType'
import { ButtonType } from 'common/builder/constants/ButtonType'
import { FBMessagingType } from 'common/builder/constants/facebookMessaging'
import { INode, IContentNode, IInstagramNode } from 'common/builder/nodes/nodeInterfaces'
import { getRootPreInteractionZoneTargets } from 'common/builder/selectors/builder/builderStateSelectors'
import { getRootChannelsInfo } from 'common/builder/selectors/builder/builderStateSelectorsTyped'
import {
  getNodesList,
  getBlocks,
  getButtons,
} from 'common/builder/selectors/builder/entitySelectors'
import * as flowSelectors from 'common/flow/selectors/flowSelectors'
import { RN_BUNDLE_MAXIMUM_BLOCKS_COUNT } from 'common/oneTimeNotify/constants/notifyReasonConstants'

export const getById = (state: RootState, flowId: string | number): IPost => {
  return state.posts.byId[flowId]
}

export const getPostDependencies = (state: RootState, flowId: string) => {
  const post = getById(state, flowId)
  return post?.dependencies || null
}

export const getPostMessageTemplateDependencies = (state: RootState, flowId: string) => {
  const dependencies = getPostDependencies(state, flowId)
  return dependencies?.message_templates || null
}

export const getListMeta = (state: RootState, listName: ListName): IPostsList => {
  return state.posts[listName]
}

export const getListItems = (state: RootState, listName: ListName): IPost[] => {
  const statuses = ListNameStatusMap[listName]
  const filtered = filter(state.posts.byId, (item) => includes(statuses, item.status))

  if (listName === ListName.SCHEDULED) {
    return sortBy(filtered, (item) => item.scheduleUpdated)
  }

  return sortBy(filtered, (item) => -item.updated)
}

export const getListItemsWithFlow = (state: RootState, listName: ListName): IPost[] => {
  const posts = getListItems(state, listName)
  return posts.map((post) => ({
    ...post,
    flow: flowSelectors.getById(state, post.flowId),
  }))
}

export const hasNotAnyBroadcasts = (state: RootState): boolean => {
  const scheduleItems = getListItemsWithFlow(state, ListName.SCHEDULED)
  const draftItems = getListItemsWithFlow(state, ListName.DRAFTS)
  const historyItems = getListItemsWithFlow(state, ListName.HISTORY)
  const areDraftItemsLoading = getListMeta(state, ListName.SCHEDULED).loading
  const areScheduleItemsLoading = getListMeta(state, ListName.DRAFTS).loading
  const areHistoryItemsLoading = getListMeta(state, ListName.HISTORY).loading

  const hasNotItems = !scheduleItems.length && !draftItems.length && !historyItems.length
  const isLoaded = !areDraftItemsLoading && !areScheduleItemsLoading && !areHistoryItemsLoading

  return hasNotItems && isLoaded
}

const AllowedNodeTypes = [NodeType.INSTAGRAM, NodeType.CONTENT, NodeType.WHATS_APP]

export const getIsSimplifiedBroadcast = createSelector(
  [
    (state: RootState) => state,
    (state: RootState, builderId: string | null | undefined, isBuilderReady: boolean) =>
      [builderId, isBuilderReady] as const,
  ],
  (state, [builderId, isBuilderReady]): null | boolean => {
    const isNodeTypeInstagramOrContent = (node: INode): node is IContentNode | IInstagramNode =>
      node.nodeType === NodeType.INSTAGRAM || node.nodeType === NodeType.CONTENT

    if (!builderId) {
      return null
    }

    if (!isBuilderReady) {
      return null
    }

    const nodes = getNodesList(state, builderId)

    const nodesWithoutStartingStep = nodes.reduce<INode[]>((nodesWithoutStartingStep, node) => {
      if (node.nodeType !== NodeType.STARTING_STEP) {
        return [...nodesWithoutStartingStep, node]
      }

      return nodesWithoutStartingStep
    }, [])

    if (nodesWithoutStartingStep.length === 0 || nodesWithoutStartingStep.length > 1) {
      return false
    }

    const node = nodesWithoutStartingStep[0]

    if (!AllowedNodeTypes.includes(node.nodeType)) {
      return false
    }

    if (isNodeTypeInstagramOrContent(node) && !node.reasonId) {
      return false
    }

    if (
      isNodeTypeInstagramOrContent(node) &&
      node.$fbMessagingType !== FBMessagingType.OUTSIDE_24_HOURS
    ) {
      return false
    }

    if (node.nodeType === NodeType.WHATS_APP) {
      return true
    }

    const { quickReply, blocksWithoutQuickReply } = getBlocks(state, builderId, node.id).reduce<{
      quickReply: IBlock | null
      blocksWithoutQuickReply: IBlock[]
    }>(
      (blockGroups, block) => {
        if (block.type === BlockType.QUICK_REPLY) {
          blockGroups.quickReply = block
        } else {
          blockGroups.blocksWithoutQuickReply.push(block)
        }

        return blockGroups
      },
      { quickReply: null, blocksWithoutQuickReply: [] },
    )

    if (quickReply && quickReply.buttons.length > 0) {
      return false
    }

    const maximumBlocksLength = RN_BUNDLE_MAXIMUM_BLOCKS_COUNT

    if (
      blocksWithoutQuickReply.length === 0 ||
      blocksWithoutQuickReply.length > maximumBlocksLength
    ) {
      return false
    }

    const forbiddenBlocks = blocksWithoutQuickReply.filter((block) => {
      if (block.type === BlockType.CARDS) {
        return false
      }

      if (block.type === BlockType.TEXT) {
        return false
      }

      if (
        block.type === BlockType.ATTACHMENT &&
        block.attachmentType === AttachmentBlockAttachmentType.IMAGE
      ) {
        return false
      }

      return true
    })

    if (forbiddenBlocks.length > 0) {
      return false
    }

    const blocksHaveOnlySimpleButtons = blocksWithoutQuickReply.every((block) => {
      return getButtons(state, builderId, block.id).every((button) =>
        /**
         * Content type is default type when user creates a button.
         */ [ButtonType.URL, ButtonType.CONTENT].includes(button.type),
      )
    })

    if (!blocksHaveOnlySimpleButtons) {
      return false
    }

    return true
  },
)

export const getHasNodeWithInside24HoursContentType = createSelector(
  [(state: RootState, builderId: string) => getRootPreInteractionZoneTargets(state, builderId)],
  (nodes) => {
    const firstIgNode = nodes.find((node) => node.nodeType === NodeType.INSTAGRAM) as
      | IInstagramNode
      | undefined

    if (!firstIgNode) {
      return false
    }

    return firstIgNode.$fbMessagingType === FBMessagingType.INSIDE_24_HOURS
  },
)

export const getHideFirstFilterGroup = createSelector(
  [(state: RootState, builderId: string) => getRootChannelsInfo(state, builderId)],
  (channelsInfo) => {
    if (channelsInfo.isMixed) {
      return false
    }

    return (
      channelsInfo.hasContent ||
      channelsInfo.hasTelegram ||
      channelsInfo.hasInstagram ||
      channelsInfo.hasSMS ||
      channelsInfo.hasEmail ||
      channelsInfo.hasWhatsApp
    )
  },
)
